import React, { useMemo } from "react";
import { Alert } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useBayHireOperationsQueue } from "@features/bay-hire/hooks/useBayHireOperationsQueue";
import type { BayHireQueueRow } from "@features/bay-hire/types";
import {
  bayHirePaymentTypeLabel,
  bayHirePreferenceLabel,
} from "@features/bay-hire/utils/bayHireLabels";
import { formatBayHireDateTime } from "@features/bay-hire/utils/formatBayHireDate";
import { getBayHireStatusBadgeType } from "@features/bay-hire/utils/statusBadge";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

/**
 * Megaplan (2026-08-05, Operations 3-tab restructure): the "History" tab of
 * BayHireOperationsTabs - resolved/terminal Bay Hire requests (APPROVED,
 * REJECTED, CANCELLED). Splits these out of the Ongoing tab, which
 * previously mixed them in with genuinely active requests (REQUESTED,
 * PRE_APPROVED, PAYED) under a subtitle that says "awaiting Operations
 * review" - see useBayHireOperationsQueue.tsx's STATUSES_BY_VIEW for the
 * full 3-way partition this restructure introduces.
 *
 * Deliberately architecturally IDENTICAL to BayHireOperationsQueue (the
 * closer sibling, since both offer a `status` Filters field unlike Waiting
 * list) - same 9-column shape, same useBayHireOperationsQueue hook (fed a
 * 3rd status set), same Filters panel wiring, same localStorage-persisted
 * page/pageSize/filters pattern, same row-click-to-detail behaviour. No new
 * columns and no different sorting for History in this pass.
 */
const BayHireHistoryQueue: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    rows,
    isLoading,
    isError,
    page,
    pageSize,
    totalCount,
    onPageChange,
    filters,
    availableFields,
    handleApplyFilters,
    handleClearFilters,
  } = useBayHireOperationsQueue("HISTORY");

  const tableRows = useMemo(
    () =>
      rows.map((row: BayHireQueueRow) => ({
        id: row.request.id,
        requestId: row.request.id,
        // FE-17/27: the visible "ID" column reads this, not `id` (a UUID kept only for row
        // navigation - canPreview reads `id` directly, unaffected by the header's own `field`).
        requestNo: row.request.requestNo ?? "-",
        numberOfBays: row.request.numberOfBays ?? "-",
        responsiblePersonName: row.request.responsiblePersonName,
        bayPreference: row.request.bayPreference,
        paymentType: row.request.paymentType,
        dateFrom: row.request.dateFrom,
        dateTo: row.request.dateTo,
        // DFT-0044 (FE-29): DD/MM/YYYY HH:MM, matching every other Marhub feature's own
        // date+time column (see formatBayHireDate.ts's formatBayHireDateTime).
        createdAt: formatBayHireDateTime(row.request.createdAt),
        status: row.request.status,
        // DFT-0027 (Slice E2): explicit badgeType so columns.tsx renders the
        // colored dot for Bay Hire's raw domain statuses (see statusBadge.ts).
        badgeType: getBayHireStatusBadgeType(row.request.status),
        // DFT-0044 (FE-19): humanized display text, read by columns.tsx's status column.
        statusLabel: t(`bay-hire.status.${row.request.status}`, {
          defaultValue: row.request.status,
        }),
        // DFT-0044: humanized display text for the Request Bay/Payment type columns.
        bayPreferenceLabel: bayHirePreferenceLabel(t, row.request.bayPreference),
        paymentTypeLabel: bayHirePaymentTypeLabel(t, row.request.paymentType),
      })),
    [rows, t],
  );

  // Same Figma p13 column set as the Ongoing/Waiting-list tabs (identical `flex` values).
  const headers: Header[] = useMemo(
    () => [
      { field: "requestNo", columnName: t("bay-hire.table.id"), flex: 0.6 },
      {
        field: "responsiblePersonName",
        columnName: t("bay-hire.table.responsiblePerson"),
        flex: 1.6,
      },
      {
        field: "numberOfBays",
        columnName: t("bay-hire.table.bayNo"),
        flex: 0.6,
      },
      {
        field: "bayPreferenceLabel",
        columnName: t("bay-hire.table.requestBay"),
        flex: 0.9,
      },
      {
        field: "paymentTypeLabel",
        columnName: t("bay-hire.table.paymentType"),
        flex: 0.8,
      },
      {
        field: "dateFrom",
        columnName: t("bay-hire.table.dateFrom"),
        type: "date",
        flex: 0.8,
      },
      {
        field: "dateTo",
        columnName: t("bay-hire.table.dateTo"),
        type: "date",
        flex: 0.8,
      },
      {
        field: "createdAt",
        columnName: t("bay-hire.table.dateSubmitted"),
        flex: 1.0,
      },
      {
        // Field literally "status" (not renderAsChip) so columns.tsx renders
        // the colored-dot Badge from this row's badgeType.
        field: "status",
        columnName: t("bay-hire.table.status"),
        flex: 0.9,
      },
    ],
    [t],
  );

  const queueContent = (
    <div style={{ position: "relative" }}>
      <LoadingOverlay loading={isLoading} testId="bay-hire-history-loading" />
      <CustomDataTable
        testId="bay-hire-history-queue-table"
        title={t("bay-hire.history-title")}
        actionName=""
        headers={headers}
        rows={tableRows}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        totalCount={totalCount}
        onPageChange={onPageChange}
        // ⋮ menu with "View details" only - the lifecycle actions stay on the detail header, and
        // a resolved/terminal request has none left to offer anyway.
        actionColumn={{
          actionsDisplay: "menu",
          headerName: t("bay-hire.table.action"),
          actions: [
            {
              label: t("bay-hire.view-details"),
              testId: (row) => `bay-hire-history-view-details-${row.id}`,
              onClick: (row) => navigate(`/${paths.bayHire.requestDetailBase}/${row.id}`),
            },
          ],
        }}
        // A row click navigates to the routed detail page, same as the Ongoing/Waiting-list tabs.
        canPreview
        navigationLinkBase={`/${paths.bayHire.requestDetailBase}`}
        filters={filters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        availableFields={availableFields}
        hideTitle
      />
    </div>
  );

  // No PageLayout/Subheader here - BayHireOperationsTabs owns the page chrome for all 3 tabs.
  return (
    <div className="mt-8 px-24" data-testid="bay-hire-history-queue">
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="bay-hire-history-load-error">
          {t("bay-hire.history-load-error")}
        </Alert>
      )}

      {queueContent}
    </div>
  );
};

export default BayHireHistoryQueue;
